import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Share2, Copy, Download, Loader2, Twitter, Facebook } from "lucide-react";

/** Shareable recap card for a settled contest. */
export const RecapShare = ({ contestId }) => {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const base = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let alive = true;
    api
      .get(`/contests/${contestId}/recap`)
      .then(({ data }) => alive && setRecap(data))
      .catch(() => alive && setRecap(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [contestId]);

  if (loading) {
    return (
      <div className="border border-white/10 bg-[#141414] p-5 text-white/40 animate-pulse">
        Building the recap card...
      </div>
    );
  }
  if (!recap) return null;

  const cardUrl = `${base}${recap.card_url}`;
  const shareText = `${recap.caption}\n${recap.share_url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Caption and link copied");
    } catch {
      toast.error("Couldn't copy — select the text instead");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title: "PickemSports recap", text: recap.caption, url: recap.share_url });
    } catch {
      /* user dismissed */
    }
  };

  return (
    <div className="border border-white/10 bg-[#141414] p-5" data-testid="recap-share">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-1">Final</div>
          <h3 className="font-display text-2xl inline-flex items-center gap-2">
            <Share2 size={18} /> SHARE THE RECAP
          </h3>
        </div>
        <div className="text-right">
          <div className="text-white/40 text-[10px] uppercase tracking-widest">Winner</div>
          <div className="font-display text-xl">{recap.winner_name}</div>
        </div>
      </div>

      <img
        src={cardUrl}
        alt={`${recap.winner_name} won with ${recap.winner_score} points`}
        className="w-full border border-white/10 mb-3"
        data-testid="recap-card-image"
      />

      <p className="text-white/70 text-sm mb-3" data-testid="recap-caption">
        {recap.caption}
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={nativeShare}
          data-testid="recap-share-btn"
          className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
        >
          <Share2 size={13} /> Share
        </button>
        <button
          onClick={copy}
          data-testid="recap-copy-btn"
          className="bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
        >
          <Copy size={13} /> Copy caption
        </button>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(recap.caption)}&url=${encodeURIComponent(recap.share_url)}`}
          target="_blank"
          rel="noreferrer"
          data-testid="recap-x-link"
          className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white/70"
          title="Share on X"
        >
          <Twitter size={15} />
        </a>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(recap.share_url)}`}
          target="_blank"
          rel="noreferrer"
          data-testid="recap-fb-link"
          className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white/70"
          title="Share on Facebook"
        >
          <Facebook size={15} />
        </a>
        <a
          href={cardUrl}
          download={`pickemsports-recap-${contestId}.png`}
          data-testid="recap-download"
          className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white/70"
          title="Download card"
        >
          <Download size={15} />
        </a>
      </div>
    </div>
  );
};
